import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn';

/**
 * `StatusPill` — semantic intent pill for surfacing state to the user.
 *
 * Intent → color contract (depends on #2405 semantic color tokens):
 *
 *   | intent  | background                          | text                        |
 *   |---------|-------------------------------------|-----------------------------|
 *   | success | color-mix(--color-success 15%)      | --color-success             |
 *   | warning | color-mix(--color-warning 15%)      | --color-warning             |
 *   | error   | color-mix(--color-error 15%)        | --color-error               |
 *   | info    | color-mix(--color-info 15%)         | --color-info                |
 *   | neutral | bg-white/[0.06]                     | text-white/60               |
 *
 * Token names (`--color-success`, `--color-warning`, `--color-error`,
 * `--color-info`) are defined in globals.css by PR #2405 — they resolve
 * to nothing until that PR merges (expected; #2405 merges first).
 * Fallback hues keep the component legible during development.
 *
 * Optionally shows a dot indicator before the label (default: on).
 *
 * `app-region-no-drag` is NOT included — StatusPill is display-only.
 * Add it at the call site if placed in an Electron drag-region header.
 */
const statusPillVariants = cva(
  'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-medium leading-none transition-colors',
  {
    variants: {
      intent: {
        success:
          'bg-[color-mix(in_srgb,var(--color-success,#4ade80)_15%,transparent)] text-[var(--color-success,#4ade80)]',
        warning:
          'bg-[color-mix(in_srgb,var(--color-warning,#facc15)_15%,transparent)] text-[var(--color-warning,#facc15)]',
        error:
          'bg-[color-mix(in_srgb,var(--color-error,#f87171)_15%,transparent)] text-[var(--color-error,#f87171)]',
        info: 'bg-[color-mix(in_srgb,var(--color-info,#60a5fa)_15%,transparent)] text-[var(--color-info,#60a5fa)]',
        neutral: 'bg-white/[0.06] text-white/60',
      },
    },
    defaultVariants: {
      intent: 'neutral',
    },
  },
);

const dotVariants = cva('h-1.5 w-1.5 shrink-0 rounded-full', {
  variants: {
    intent: {
      success: 'bg-[var(--color-success,#4ade80)]',
      warning: 'bg-[var(--color-warning,#facc15)]',
      error: 'bg-[var(--color-error,#f87171)]',
      info: 'bg-[var(--color-info,#60a5fa)]',
      neutral: 'bg-white/40',
    },
  },
  defaultVariants: {
    intent: 'neutral',
  },
});

export interface StatusPillProps
  extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof statusPillVariants> {
  /** Show/hide the dot indicator before the label (default: true). */
  dot?: boolean;
  /**
   * Apply `backdrop-blur-sm` so the pill reads over tile images
   * (e.g. the Dashboard card overlay pattern).
   */
  glass?: boolean;
}

export function StatusPill({
  intent,
  dot = true,
  glass = false,
  className,
  children,
  ...props
}: StatusPillProps): React.JSX.Element {
  return (
    <span
      className={cn(statusPillVariants({ intent }), glass && 'backdrop-blur-sm', className)}
      {...props}
    >
      {dot && <span className={dotVariants({ intent })} />}
      {children}
    </span>
  );
}

export { statusPillVariants };
